import { tKey, type I18nKey } from "@/core/i18n";

/**
 * Helpers for reading and patching individual keys inside the free-form
 * `customHyperparams` JSON string.
 *
 * The blob is the single source of truth: presets write it, the AI suggestion
 * rewrites it, and the hyperparams editor edits it directly. Any dedicated
 * control for one key (today: model size) therefore derives its value from the
 * blob and patches back into it, rather than holding parallel state that would
 * silently disagree the moment a preset is applied.
 */

/** Returns null when the string isn't a JSON object — controls disable on null. */
export const parseHyperparamsObject = (
  value: string,
): Record<string, unknown> | null => {
  if (!value.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/** Set one key in the blob, preserving the rest and the 2-space formatting. */
export const patchHyperparam = (
  value: string,
  key: string,
  next: unknown,
): string | null => {
  const parsed = parseHyperparamsObject(value);
  if (!parsed) return null;
  return JSON.stringify({ ...parsed, [key]: next }, null, 2);
};

export type ModelSize = "nano" | "small" | "medium" | "large" | "xlarge";

/**
 * Sizes offered in the UI. `xlarge` is deliberately absent: it is accepted on
 * the wire (and by ml-yolo) but costs enough training time that it should be a
 * deliberate choice typed into the hyperparams editor, not a click.
 */
export const SELECTABLE_MODEL_SIZES: ModelSize[] = [
  "nano",
  "small",
  "medium",
  "large",
];

export const MODEL_SIZE_LABELS = {
  nano: tKey("model:modelSize.labels.nano"),
  small: tKey("model:modelSize.labels.small"),
  medium: tKey("model:modelSize.labels.medium"),
  large: tKey("model:modelSize.labels.large"),
  xlarge: tKey("model:modelSize.labels.xlarge"),
} satisfies Record<ModelSize, I18nKey<"model">>;

export const MODEL_SIZE_HINTS = {
  nano: tKey("model:modelSize.hints.nano"),
  small: tKey("model:modelSize.hints.small"),
  medium: tKey("model:modelSize.hints.medium"),
  large: tKey("model:modelSize.hints.large"),
  xlarge: tKey("model:modelSize.hints.xlarge"),
} satisfies Record<ModelSize, I18nKey<"model">>;

// Models created before the size rename store a checkpoint name instead. Map
// it back so duplicating one shows the size it was actually trained at.
const LEGACY_VARIANT_TO_SIZE: Record<string, ModelSize> = {
  n: "nano",
  s: "small",
  m: "medium",
  l: "large",
  x: "xlarge",
};

/** Current size from the blob, falling back to the pre-rename spelling. */
export const readModelSize = (
  params: Record<string, unknown> | null,
): ModelSize | null => {
  if (!params) return null;

  const size = params.model_size;
  if (typeof size === "string" && size in MODEL_SIZE_LABELS) {
    return size as ModelSize;
  }

  const variant = params.model_variant;
  if (typeof variant === "string") {
    const match = /^yolo11([nsmlx])(?:-(?:seg|cls))?$/i.exec(
      variant.replace(/\.pt$/i, "").trim(),
    );
    if (match) return LEGACY_VARIANT_TO_SIZE[match[1].toLowerCase()];
  }

  return null;
};

/**
 * Layer freezing, expressed as `freeze` (how many backbone modules to hold
 * fixed so only the head trains).
 *
 * Tri-state on purpose. The training service applies its own small-dataset
 * default when the key is absent, so a plain on/off switch would show "off"
 * while the run actually froze the backbone. "Auto" names that absence
 * honestly; On/Off write a value and take the decision away from the service.
 */
export type FreezeMode = "auto" | "on" | "off";

/** Modules 0-10 are the backbone; the detection head starts at 11. */
export const FREEZE_BACKBONE_LAYERS = 10;

export const FREEZE_MODES: FreezeMode[] = ["auto", "on", "off"];

export const FREEZE_MODE_LABELS = {
  auto: tKey("model:freezeMode.auto"),
  on: tKey("model:freezeMode.on"),
  off: tKey("model:freezeMode.off"),
} satisfies Record<FreezeMode, I18nKey<"model">>;

export const readFreezeMode = (
  params: Record<string, unknown> | null,
): FreezeMode => {
  if (!params) return "auto";
  const freeze = params.freeze;
  if (typeof freeze !== "number") return "auto";
  return freeze > 0 ? "on" : "off";
};

/** Returns the patched blob, or null when it isn't parseable JSON. */
export const writeFreezeMode = (
  value: string,
  mode: FreezeMode,
): string | null => {
  const parsed = parseHyperparamsObject(value);
  if (!parsed) return null;
  if (mode === "auto") {
    // Absence is the signal — deleting the key hands the decision back.
    const { freeze: _omitted, ...rest } = parsed;
    return JSON.stringify(rest, null, 2);
  }
  return JSON.stringify(
    { ...parsed, freeze: mode === "on" ? FREEZE_BACKBONE_LAYERS : 0 },
    null,
    2,
  );
};
